import { apiService } from '../Config/api.service';

let cachedUser = null;
let cachedUserPromise = null;

export const getStoredUser = () => {
    try {
        return JSON.parse(localStorage.getItem('user') || '{}');
    } catch (error) {
        return {};
    }
};

// L'offre vit aussi sous sa propre clé : une relecture de `/user` réécrit
// l'utilisateur stocké (la barre latérale le fait), jamais l'offre.
const CLE_OFFRE = 'offre_rh';
// Le droit « Correction des pointages » (YRH-CORE-013) prend le même chemin,
// pour la même raison : `/user` relu ne porte rien de C2.
const CLE_CORRECTION = 'corriger_pointages';
// La capacité manager (Yros/Kavero#361), pour la même raison.
const CLE_MANAGER = 'manager';
// Le validateur des heures sup du pointage (Yros/Kavero#491), pour la même raison.
const CLE_VALIDATEUR_HEURES_SUP = 'valider_heures_sup';

/** Mémorise l'identité résolue (identite.js), son offre et son droit à part. */
export const memoriserIdentite = (utilisateur) => {
    localStorage.setItem('user', JSON.stringify(utilisateur));
    localStorage.setItem(CLE_OFFRE, String(utilisateur?.offre_rh === true));
    // La clé n'existe que vraie : une identité qui n'a pas le droit efface celui
    // d'une session précédente, et sans clé le droit est refusé.
    if (utilisateur?.corriger_pointages === true) {
        localStorage.setItem(CLE_CORRECTION, 'true');
    } else {
        localStorage.removeItem(CLE_CORRECTION);
    }
    if (utilisateur?.manager === true) {
        localStorage.setItem(CLE_MANAGER, 'true');
    } else {
        localStorage.removeItem(CLE_MANAGER);
    }
    if (utilisateur?.valider_heures_sup === true) {
        localStorage.setItem(CLE_VALIDATEUR_HEURES_SUP, 'true');
    } else {
        localStorage.removeItem(CLE_VALIDATEUR_HEURES_SUP);
    }
};

/** L'offre de l'identité mémorisée : vrai, faux, ou undefined si aucune. */
const offreMemorisee = () => {
    const valeur = localStorage.getItem(CLE_OFFRE);
    return valeur === null ? undefined : valeur === 'true';
};

/** Le droit de correction de l'identité mémorisée : vrai seulement si posé. */
const correctionMemorisee = () => localStorage.getItem(CLE_CORRECTION) === 'true';

/** La capacité manager de l'identité mémorisée : vraie seulement si posée. */
const managerMemorise = () => localStorage.getItem(CLE_MANAGER) === 'true';

/** Le validateur des heures sup de l'identité mémorisée : vrai seulement si posé. */
const validateurMemorise = () => localStorage.getItem(CLE_VALIDATEUR_HEURES_SUP) === 'true';

export const fetchCurrentUser = async (force = false) => {
    if (!force && cachedUser) {
        return cachedUser;
    }

    if (!force && cachedUserPromise) {
        return cachedUserPromise;
    }

    cachedUserPromise = apiService
        .getElement('/user')
        .then((response) => {
            cachedUser = response?.data?.user || {};
            cachedUserPromise = null;
            return cachedUser;
        })
        .catch(() => {
            cachedUserPromise = null;
            return getStoredUser();
        });

    return cachedUserPromise;
};

export const resetCachedUser = () => {
    cachedUser = null;
    cachedUserPromise = null;
};

export const getUserRole = (user = getStoredUser()) => user?.role || 'user';

// 'compta' et 'user' (Client Cabinet) sont hors hiérarchie : niveau 0, en consultation seule.
const ROLE_LEVELS = { user: 0, compta: 0, rh: 1, admin: 2, superadmin: 3 };

// Contrat C3 : `employee` n'est PAS un échelon et n'entre pas dans
// ROLE_LEVELS. L'y insérer lui donnerait mécaniquement tout ce qu'obtient
// aujourd'hui le rôle `user`, sans qu'aucun `canXxx` ne change visiblement.
// Le gating de ce rôle est donc ce prédicat plus une liste blanche de routes,
// jamais une comparaison d'échelons via hasMinRole.
export const isEmployee = (user) => getUserRole(user) === 'employee';

const hasMinRole = (user, minRole) =>
    (ROLE_LEVELS[getUserRole(user)] ?? 0) >= (ROLE_LEVELS[minRole] ?? 0);

const resolveUser = (user) => user || getStoredUser();

// Capacité manager (Yros/Kavero#361) : un salarié qui a une équipe, sur une
// société qui a le RH. Fermée au moindre doute ; confort d'interface, la
// barrière est l'équipe que rend Odoo (`_yros_rh_equipe()`). Utilisateur reçu
// d'abord, puis la capacité mémorisée à part, comme `canCorrigerPointages`.
export const isManager = (user) => {
    const resolu = resolveUser(user);
    return isEmployee(resolu) && aOffreRh(user)
        && (user?.manager ?? managerMemorise()) === true;
};

// Validateur des heures sup du pointage (Yros/Kavero#491) : le validateur
// désigné de la société, RH ou salarié, ou à défaut la RH. Confort
// d'interface : la barrière est `_yros_rh_valide_heures_sup()` côté Odoo.
export const canValiderHeuresSup = (user) =>
    aOffreRh(user) && (user?.valider_heures_sup ?? validateurMemorise()) === true;

// Offre souscrite (YRH-FOUND-014) : une société Paie seule ne voit rien de ce
// que le RH a ajouté. L'offre voyage dans l'identité (identite.js), qui écrit
// toujours un booléen et masque au moindre doute sur C2. Un `/user` relu plus
// tard ne la porte pas : on retombe alors sur l'offre mémorisée à part — la
// barre latérale réécrit l'utilisateur stocké —, puis sur l'utilisateur
// stocké. Seul un faux explicite masque. Confort d'interface : la barrière
// est côté Odoo (contrat C19).
export const aOffreRh = (user) =>
    (user?.offre_rh ?? offreMemorisee() ?? getStoredUser().offre_rh) !== false;

const peutRh = (user) => aOffreRh(user) && hasMinRole(resolveUser(user), 'rh');

// Seuil `rh` : la RH configure le contrôle de zone du pointage depuis cet
// écran (Yros/Kavero#358). La porte ne garde donc plus rien à elle seule —
// c'est chaque onglet qui porte sa propre capacité, dans visibleMenuItems.
export const canAccessSettings      = (user) => hasMinRole(resolveUser(user), 'rh');
export const canManageCompany       = (user) => hasMinRole(resolveUser(user), 'admin');
export const canCreateCompany       = (user) => {
    const resolvedUser = resolveUser(user);
    if (hasMinRole(resolvedUser, 'superadmin')) return true;
    if (!hasMinRole(resolvedUser, 'admin')) return false;
    const activeId = resolvedUser?.company_active?.id;
    const activeInList = (resolvedUser?.company_ids || []).find(c => c.id === activeId);
    return activeInList?.parent_id === 1;
};
export const canCreateUser          = (user) => hasMinRole(resolveUser(user), 'admin');
export const canCloseMonth          = (user) => hasMinRole(resolveUser(user), 'rh');
// La RH supprime un solde de tout compte en brouillon (Yros/Support#50) ; le
// serveur refuse un solde dont les indemnités figurent sur un bulletin validé.
export const canDeleteSettlement    = (user) => hasMinRole(resolveUser(user), 'rh');
export const canManageAccountingSetup = (user) => hasMinRole(resolveUser(user), 'admin');
export const canManageUsers         = (user) => hasMinRole(resolveUser(user), 'admin');
export const canManageContractExpiration = (user) => hasMinRole(resolveUser(user), 'rh');
export const canImportPayroll       = (user) => peutRh(user);
// Configuration du contrôle de zone du pointage (C13). Prédicat nommé, pas un
// alias de canImportPayroll : deux capacités au même échelon aujourd'hui
// n'ont aucune raison de le rester (plan bolt 034).
export const canConfigurerPointage  = (user) => peutRh(user);
// Droit « Correction des pointages » (YRH-CORE-013, contrat C2 étendu) :
// corriger ou ajouter un pointage change les heures de la feuille de temps,
// donc la paie. Fermé au moindre doute — clé absente, faux, chaîne, société en
// Paie seule, rôle sous `rh` — et jamais déduit du rôle : l'admin et le
// superadmin l'ont d'office côté serveur, c'est C2 qui le dit. L'utilisateur
// reçu parle d'abord ; un `/user` relu ne porte rien de C2, on retombe alors
// sur le droit mémorisé à part. Confort d'interface : la barrière est la garde
// d'Odoo (`non_autorise`).
export const canCorrigerPointages   = (user) =>
    peutRh(user) && (user?.corriger_pointages ?? correctionMemorisee()) === true;

// Présence du jour (C12, Yros/Kavero#60) : garde de confort d'interface, pas
// une autorisation — la donnée est déjà ouverte à tout compte interne côté
// proxy (review 029, § « trou du rôle user »). Seuil identique à
// canImportPayroll, prédicat nommé à part : les deux capacités n'ont aucune
// raison de rester à jamais au même échelon.
export const canSuperviserPresences = (user) => peutRh(user);

// Badge du collaborateur (contrat C26, Yros/Kavero#222). Confort d'interface :
// Odoo réserve le badge au gestionnaire RH de la société active, et le refuse
// à une société en Paie seule.
export const canImprimerBadge       = (user) => peutRh(user);
// Créer et réinviter le compte Portail d'un salarié depuis sa fiche (contrat
// C33, Yros/Kavero#470) : la RH, avec l'offre RH.
export const canGererComptePortail  = (user) => peutRh(user);
// Lire le profil enrichi d'un salarié (bio, compétences, langues) sur sa fiche
// (YRH-NEW-008-T6, Yros/Kavero#482) : la RH, avec l'offre RH ; la barrière est le
// groupe gestionnaire d'Odoo, qui borne à la société de l'appelant.
export const canVoirProfilEmploye   = (user) => peutRh(user);
// L'organigramme de la société (Yros/Kavero#494) : la RH, sous l'offre RH.
export const canConsulterOrganigramme = (user) => peutRh(user);
// Départs, solde de tout compte et domiciliations de salaire (Yros/Kavero#62) :
// gestionnaire RH avec l'offre RH ; la barrière est le groupe gestionnaire d'Odoo.
export const canGererDeparts        = (user) => peutRh(user);
// Registre des dossiers disciplinaires (C39, Yros/Kavero#15) : la RH, avec l'offre
// RH. Confort d'interface : la barrière est la garde du proxy et `non_autorise`
// côté Odoo. Prédicat nommé, pas un alias de canGererDeparts.
export const canGererDossiersDisciplinaires = (user) => peutRh(user);
// L'annuaire interne (YRH-NEW-009, Yros/Kavero#199) : tout rôle connu, `user` et
// `compta` compris, avec l'offre RH ; Odoo borne la société, le périmètre et
// les champs rendus. Un rôle inconnu ne la voit pas : le proxy le refuserait.
const ROLES_ANNUAIRE = ['employee', ...Object.keys(ROLE_LEVELS)];
export const canConsulterAnnuaire   = (user) =>
    aOffreRh(user) && ROLES_ANNUAIRE.includes(resolveUser(user)?.role);

// Éléments de rémunération (Yros/Kavero#404) : primes, avantages, notes de
// frais et surtout déductions — c'est-à-dire `excohsp.repayment_loan`, donc
// de l'argent retenu au bulletin. Prédicat nommé, pas un alias de
// `canImportPayroll` : deux capacités au même échelon aujourd'hui n'ont
// aucune raison de le rester.
export const canGererElementsRemuneration = (user) => hasMinRole(resolveUser(user), 'rh');

// Écrans de gestion de la Paie (bolt 161, suite de Yros/Kavero#404) : huit
// routes se montaient sans garde de rôle pour tout compte interne, `user`
// compris, alors que ce rôle n'a aucun écran de gestion à avoir. Le seuil est
// le rôle seul, jamais `aOffreRh` : un client Paie seule garde ses écrans.
// Seuil `rh` provisoire jusqu'à #414 ; si #414 retient un autre groupe
// d'écriture Paie, seul ce bloc bouge. Un prédicat par domaine, nommé à part,
// pour qu'aucun n'ait à suivre l'échelon d'un autre.
// Confort d'interface : la donnée n'est pas fermée côté serveur (#414).
export const canGererCollaborateurs = (user) => hasMinRole(resolveUser(user), 'rh');
export const canGererBulletins      = (user) => hasMinRole(resolveUser(user), 'rh');
export const canConsulterEtats      = (user) => hasMinRole(resolveUser(user), 'rh');
export const canGererAbsences       = (user) => hasMinRole(resolveUser(user), 'rh');
// L'accueil complet rend les totaux de paie (masse salariale, brut, impôts,
// cotisations) ; sans ce prédicat, le compte reçoit l'accueil minimal.
// Le Client Cabinet et le Comptable gardent le tableau de bord que dev leur
// donne ; `Home` y masque lui-même ce qu'ils ne peuvent pas modifier.
export const canVoirTableauDeBord   = (user) =>
    hasMinRole(resolveUser(user), 'rh') || aRoleExplicite(user, 'user') || aRoleExplicite(user, 'compta');
// Détails de paiement d'un état : `require_group` admin côté Odoo, le portail
// s'aligne sans aller plus haut.
export const canModifierDetailsPaiement = (user) => hasMinRole(resolveUser(user), 'admin');

// Supervision RH des demandes, tous types (C10, Yros/Kavero#71) : prédicat
// propre, pas un alias d'une autre capacité RH — deux capacités au
// même seuil aujourd'hui n'ont aucune raison de le rester (savoir-faire
// constructeur, bolt 034).
export const canSuperviserDemandesRh = (user) => peutRh(user);

// Feuilles de temps vues par la RH (C22, Yros/Kavero#220) : un ajout du RH,
// donc absent d'une société Paie seule. Prédicat nommé, pas un alias : confort
// d'interface seulement, la barrière est le groupe gestionnaire côté Odoo.
export const canSuperviserFeuillesTemps = (user) => peutRh(user);

// Écran Paie « Types d'absence » (bolt 123, Yros/Kavero#417) : créer ou
// changer un type d'absence modifie la paie et le rend demandable par les
// salariés. Confort d'interface seulement — la route Odoo `/api/absence_types`
// reste ouverte à tout compte interne.
export const canGererTypesAbsence = (user) => hasMinRole(resolveUser(user), 'rh');

// Émission des ordres de mission (Yros/Kavero#127) : seuil identique au
// `require_group("rh","admin","superadmin")` de la route d'émission du proxy.
// Garde d'interface seulement : la barrière est le groupe gestionnaire côté
// Odoo (Yros/Kavero#125).
export const canEmettreOrdresMission = (user) => peutRh(user);

// Offres des sociétés (Yros/Kavero#447) : le superadmin Payor, et lui seul,
// ouvre ou coupe le RH d'une société ; un admin client ne voit jamais son
// offre. Confort d'interface seulement — la barrière est le groupe
// `group_yros_gestion_offres` côté Odoo et `require_group("superadmin")` au
// proxy. Aucun regard sur l'offre de la société active : le superadmin ouvre
// le RH d'une société depuis n'importe laquelle, y compris une société en
// Paie seule.
export const canGererOffres = (user) => hasMinRole(resolveUser(user), 'superadmin');

// Attribution du droit « Correction des pointages » (YRH-CORE-013, contrat
// C27) : l'admin et le superadmin désignent les comptes RH qui corrigent. Côté
// serveur, la lecture et l'écriture sont gardées par le groupe d'administration
// RH que la Paie leur donne, et par `require_group("admin", "superadmin")` au
// proxy ; le rôle `rh` ne se désigne pas lui-même. Un ajout du RH, donc absent
// d'une société Paie seule (C19) : les pointages n'y existent pas. Confort
// d'interface seulement.
export const canGererDroitsRh = (user) => aOffreRh(user) && hasMinRole(resolveUser(user), 'admin');

// Rôles en consultation seule : 'compta' (Pôle Comptabilité) et 'user' (Client Cabinet).
export const isAccountant           = (user) => getUserRole(resolveUser(user)) === 'compta';
export const isReadOnlyUser         = (user) => getUserRole(resolveUser(user)) === 'user';
export const canEditHrData          = (user) => hasMinRole(resolveUser(user), 'rh');
export const canViewAccountingJournal = (user) => canEditHrData(user) || isAccountant(user);
export const canViewEmployees       = (user) => !isAccountant(user);
export const canViewFinancialStatements = (user) => canEditHrData(user);

// Consultation seule héritée de dev (rôles Comptable et Client Cabinet) : ces
// deux rôles restent sous l'échelon `rh`, donc hors des prédicats `canGerer…`
// ci-dessus. Ils retrouvent ici les écrans que dev leur ouvrait, sans gagner
// aucune action d'écriture — celles-ci restent gardées par `canEditHrData`
// dans les pages. Le Client Cabinet consulte le tableau de bord, les
// collaborateurs et les bulletins ; le Comptable, le tableau de bord et les
// bulletins (Journal & Répartition).
// Fermé au moindre doute : le rôle doit être porté explicitement. Une identité
// absente ou sans rôle — que `getUserRole` ramène à `user` — n'ouvre rien.
const aRoleExplicite = (user, role) => resolveUser(user)?.role === role;
export const canConsulterCollaborateurs = (user) =>
    canGererCollaborateurs(user) || aRoleExplicite(user, 'user');
export const canConsulterBulletins = (user) =>
    canGererBulletins(user) || aRoleExplicite(user, 'user') || aRoleExplicite(user, 'compta');
